/**
 * Per-device conveniences only (the server is the source of truth).
 * Every access is guarded: private mode or blocked storage must not break the app.
 */
export function readJson<T>(key: string, fallback: T, store: 'local' | 'session' = 'local'): T {
  try {
    const raw = (store === 'local' ? localStorage : sessionStorage).getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writeJson(key: string, value: unknown, store: 'local' | 'session' = 'local'): void {
  try {
    (store === 'local' ? localStorage : sessionStorage).setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

export function removeKey(key: string, store: 'local' | 'session' = 'local'): void {
  try {
    (store === 'local' ? localStorage : sessionStorage).removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

export interface SavedBooking { token: string; code: string; startsAt: string; title: string; savedAt: string; status?: string }

/** Access links to the visitor's own bookings, kept on this device per studio. */
export function savedBookings(slug: string): SavedBooking[] {
  return readJson<SavedBooking[]>(`bookings:${slug}`, []);
}

export function saveBooking(slug: string, b: SavedBooking): void {
  const list = savedBookings(slug).filter((x) => x.token !== b.token);
  writeJson(`bookings:${slug}`, [b, ...list].slice(0, 20));
}

/** Keeps the device copy in sync with what the server last reported (e.g. cancelled). */
export function setBookingStatus(slug: string, token: string, status: string): void {
  const list = savedBookings(slug);
  if (!list.some((x) => x.token === token && x.status !== status)) return;
  writeJson(`bookings:${slug}`, list.map((x) => (x.token === token ? { ...x, status } : x)));
}

export function forgetBooking(slug: string, token: string): void {
  writeJson(`bookings:${slug}`, savedBookings(slug).filter((x) => x.token !== token));
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
